import React, { createContext, useState, useContext, useEffect, ReactNode, useRef } from 'react';
import authApi, { AuthResponse, UserProfile, SystemLoginRequest, GoogleLoginRequest } from '../services/authApi.ts';
import { toast } from 'react-toastify';

// Helper function to decode JWT token
const decodeJWT = (token: string): any => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
};

// Helper function to check if token is expiring soon (within 5 minutes)
const isTokenExpiringSoon = (token: string, bufferMinutes: number = 5): boolean => {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) return true;

  const expirationTime = decoded.exp * 1000; // Convert to milliseconds
  const currentTime = Date.now();
  const timeUntilExpiry = expirationTime - currentTime;
  const bufferTime = bufferMinutes * 60 * 1000; // Convert minutes to milliseconds

  return timeUntilExpiry <= bufferTime;
};

interface User {
  userId: number;
  name: string;
  email: string | null;
  username: string | null;
  userType: 'CLIENT' | 'SYSTEM';
  roles: string[];
  privileges: string[];
  profilePhoto: string | null;
  isPremium: boolean;
  planName?: string;
  expiryDate?: string;
  daysRemaining?: number;
  createdDate?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: SystemLoginRequest) => Promise<void>;
  googleLogin: (googleIdToken: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUserProfile: () => Promise<void>;
  updateUserProfile: (name: string, profilePhotoUrl: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const tokenRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Load user from localStorage on mount
  useEffect(() => {
    const loadUser = async () => {
      const storedUser = localStorage.getItem('user');
      const accessToken = authApi.getAccessToken();

      if (storedUser && accessToken) {
        try {
          // Verify token is still valid by fetching profile
          const response = await authApi.getUserProfile();
          const profile = response.data;

          // Get subscription status
          const subResponse = await authApi.getSubscription();
          const subscriptionData = subResponse.data;

          const userData: User = {
            userId: profile.userId,
            name: profile.name,
            email: profile.email,
            username: profile.userType === 'SYSTEM' ? profile.name : null,
            userType: profile.userType,
            roles: profile.roles,
            privileges: profile.privileges,
            profilePhoto: profile.profilePhoto,
            isPremium: subscriptionData.isPremium,
            planName: subscriptionData.planName,
            expiryDate: subscriptionData.expiryDate,
            daysRemaining: subscriptionData.daysRemaining,
            createdDate: subscriptionData.createdDate ?? undefined,
          };

          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
        } catch (error) {
          console.error('Failed to load user profile:', error);
          // Token might be expired, clear everything
          authApi.clearTokens();
          localStorage.removeItem('user');
        }
      }
      setIsLoading(false);
    };

    loadUser();
  }, []);

  // Automatic token refresh
  useEffect(() => {
    const checkAndRefreshToken = async () => {
      const accessToken = authApi.getAccessToken();
      const refreshToken = localStorage.getItem('refreshToken');

      if (!accessToken || !refreshToken) {
        // No tokens, clear interval
        if (tokenRefreshIntervalRef.current) {
          clearInterval(tokenRefreshIntervalRef.current);
          tokenRefreshIntervalRef.current = null;
        }
        return;
      }

      // Check if token is expiring soon (within 5 minutes)
      if (isTokenExpiringSoon(accessToken, 5)) {
        console.log('🔄 Token expiring soon, refreshing automatically...');

        try {
          const response = await authApi.refreshToken({ refreshToken });

          // Update tokens in localStorage
          authApi.saveTokens(response.data.accessToken, response.data.refreshToken);

          console.log('✅ Token refreshed successfully');
          toast.info('Session refreshed');
        } catch (error) {
          console.error('❌ Auto token refresh failed:', error);

          // Refresh failed, logout user
          toast.error('Session expired. Please login again.');

          authApi.clearTokens();
          localStorage.removeItem('user');
          setUser(null);

          // Clear interval
          if (tokenRefreshIntervalRef.current) {
            clearInterval(tokenRefreshIntervalRef.current);
            tokenRefreshIntervalRef.current = null;
          }

          // Redirect to home page
          window.location.href = '/';
        }
      }
    };

    // Start checking every minute if user is authenticated
    if (user) {
      // Check immediately
      checkAndRefreshToken();

      // Then check every 1 minute
      tokenRefreshIntervalRef.current = setInterval(checkAndRefreshToken, 60 * 1000);
    } else {
      // Clear interval if user logs out
      if (tokenRefreshIntervalRef.current) {
        clearInterval(tokenRefreshIntervalRef.current);
        tokenRefreshIntervalRef.current = null;
      }
    }

    // Cleanup on unmount
    return () => {
      if (tokenRefreshIntervalRef.current) {
        clearInterval(tokenRefreshIntervalRef.current);
      }
    };
  }, [user]);

  const login = async (credentials: SystemLoginRequest) => {
    try {
      const response = await authApi.systemLogin(credentials);
      const authData: AuthResponse = response.data;

      // Save tokens
      authApi.saveTokens(authData.accessToken, authData.refreshToken);

      // Get subscription details
      const subResponse = await authApi.getSubscription();
      const subscriptionData = subResponse.data;

      // Create user object
      const userData: User = {
        userId: authData.userId,
        name: authData.name,
        email: authData.email,
        username: authData.username,
        userType: authData.userType,
        roles: authData.roles,
        privileges: authData.privileges,
        profilePhoto: null,
        isPremium: subscriptionData.isPremium,
        planName: subscriptionData.planName,
        expiryDate: subscriptionData.expiryDate,
        daysRemaining: subscriptionData.daysRemaining,
        createdDate: subscriptionData.createdDate ?? undefined,
      };

      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));

      toast.success('Login successful!');
    } catch (error: any) {
      console.error('Login failed:', error);
      const errorMessage = error.response?.data?.message || 'Invalid username or password';
      toast.error(errorMessage);
      throw error;
    }
  };

  const googleLogin = async (googleIdToken: string) => {
    try {
      const request: GoogleLoginRequest = { googleIdToken };
      const response = await authApi.googleLogin(request);
      const authData: AuthResponse = response.data;

      // Save tokens
      authApi.saveTokens(authData.accessToken, authData.refreshToken);

      // Get subscription details
      const subResponse = await authApi.getSubscription();
      const subscriptionData = subResponse.data;

      // Create user object
      const userData: User = {
        userId: authData.userId,
        name: authData.name,
        email: authData.email,
        username: null,
        userType: authData.userType,
        roles: authData.roles,
        privileges: authData.privileges,
        profilePhoto: null,
        isPremium: subscriptionData.isPremium,
        planName: subscriptionData.planName,
        expiryDate: subscriptionData.expiryDate,
        daysRemaining: subscriptionData.daysRemaining,
        createdDate: subscriptionData.createdDate ?? undefined,
      };

      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));

      toast.success('Google login successful!');
    } catch (error: any) {
      console.error('Google login failed:', error);
      const errorMessage = error.response?.data?.message || 'Google login failed';
      toast.error(errorMessage);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
      setUser(null);
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout failed:', error);
      // Clear tokens anyway
      authApi.clearTokens();
      localStorage.removeItem('user');
      setUser(null);
    }
  };

  const refreshUserProfile = async () => {
    try {
      const response = await authApi.getUserProfile();
      const profile = response.data;

      // Get subscription status
      const subResponse = await authApi.getSubscription();
      const subscriptionData = subResponse.data;

      const userData: User = {
        userId: profile.userId,
        name: profile.name,
        email: profile.email,
        username: profile.userType === 'SYSTEM' ? profile.name : null,
        userType: profile.userType,
        roles: profile.roles,
        privileges: profile.privileges,
        profilePhoto: profile.profilePhoto,
        isPremium: subscriptionData.isPremium,
        planName: subscriptionData.planName,
        expiryDate: subscriptionData.expiryDate,
        daysRemaining: subscriptionData.daysRemaining,
        createdDate: subscriptionData.createdDate ?? undefined,
      };

      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
    } catch (error) {
      console.error('Failed to refresh user profile:', error);
      throw error;
    }
  };

  const updateUserProfile = async (name: string, profilePhotoUrl: string) => {
    try {
      await authApi.updateUserProfile({ name, profilePhotoUrl });
      await refreshUserProfile();
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error('Failed to update profile');
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    googleLogin,
    logout,
    refreshUserProfile,
    updateUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
